import { toast } from 'react-toastify'
import * as Api from 'src/api'
import { Fn } from 'src/hooks/locale/utils'

/** Form values for the General tab only */
export interface SectionFormValues {
  readonly name: string
  readonly nameEn: string
  readonly workTitle: string
  readonly workTitleEn: string
  readonly min: number
  readonly max: number
  readonly degree: string | undefined
  readonly status: string | undefined
  readonly workType: string | undefined
}

/** Form values for the Competencies tab only */
export interface CriteriaFormValues {
  readonly id: string
  readonly min: number
  readonly max: number
}

export interface CompetencyFormValues {
  readonly id: string
  readonly name: string
  readonly minScore: number
  readonly maxScore: number
  readonly criterias: CriteriaFormValues[]
}

/** Form values for the Semesters tab only */
export interface MentorFormValues {
  readonly profileId: string
  readonly quota: number
}

export interface SemesterFormValues {
  readonly semester: string
  readonly amount: number
  readonly mentors: MentorFormValues[]
}

const sectionNull = (s: Api.AdministratorPortfolioSectionDetails | null) => s ?? null

export function getSectionInitialValues(section: Api.AdministratorPortfolioSectionDetails | null): {
  section: SectionFormValues
} {
  const s = sectionNull(section)
  return {
    section: {
      name: s?.name ?? '',
      nameEn: s?.nameEn ?? '',
      min: s?.min ?? 0,
      max: s?.max ?? 0,
      degree: s?.degree?.value.toString(),
      status: s?.status?.value.toString(),
      workType: s?.workType?.value,
      workTitle: s?.workTitle ?? '',
      workTitleEn: s?.workTitleEn ?? '',
    },
  }
}

export function getCompetenciesInitialValues(section: Api.AdministratorPortfolioSectionDetails | null): {
  competencies: CompetencyFormValues[]
} {
  const s = sectionNull(section)
  const competencies: CompetencyFormValues[] = (s?.competencies ?? []).map((comp) => ({
    id: comp.id,
    name: comp.name,
    minScore: comp.minScore ?? 0,
    maxScore: comp.maxScore ?? 0,
    criterias: (comp.criterias ?? []).map((crit) => ({
      id: crit.id,
      min: crit.pivot?.min ?? 0,
      max: crit.pivot?.max ?? 0,
    })),
  }))
  return { competencies }
}

export function getSemestersInitialValues(section: Api.AdministratorPortfolioSectionDetails | null): {
  semesters: SemesterFormValues[]
} {
  const s = sectionNull(section)
  const mentorsBySemester = (s?.mentors ?? []).reduce(
    (acc, mentor) => {
      const semesterNum = mentor.pivot.semester
      if (!acc[semesterNum]) acc[semesterNum] = []
      acc[semesterNum].push({
        profileId: mentor.profileId.toString(),
        quota: mentor.pivot.quota,
      })
      return acc
    },
    {} as Record<number, MentorFormValues[]>
  )
  const semesters: SemesterFormValues[] = (s?.semesters ?? []).map((sem) => ({
    semester: sem.semester.toString(),
    amount: sem.workCount,
    mentors: mentorsBySemester[sem.semester] ?? [],
  }))
  return { semesters }
}

export function sectionFormValuesToBody(v: SectionFormValues): Api.AdministratorPortfolioSectionRequest {
  return {
    name: v.name,
    nameEn: v.nameEn,
    workTitle: v.workTitle,
    workTitleEn: v.workTitleEn,
    min: v.min,
    max: v.max,
    status: Number(v.status),
    degree: Number(v.degree),
    workType: Number(v.workType),
  }
}

export function competenciesFormValuesToBody(
  values: CompetencyFormValues[]
): Api.AdministratorPortfolioCompetencyRequest {
  return {
    id: values.map((comp) => comp.id),
    min: values.map((comp) => comp.minScore),
    max: values.map((comp) => comp.maxScore),
    criterias: values.map((comp) => ({
      id: comp.criterias.map((crit) => crit.id),
      min: comp.criterias.map((crit) => Number(crit.min)),
      max: comp.criterias.map((crit) => Number(crit.max)),
    })),
  }
}

export function semestersFormValuesToBody(values: SemesterFormValues[]): {
  semesters: Api.AdministratorPortfolioSemesterRequest[]
  mentors: Api.AdministratorPortfolioMentorRequest[][]
} {
  const semesters: Api.AdministratorPortfolioSemesterRequest[] = values.map((sem) => ({
    id: sem.semester,
    amount: sem.amount,
  }))
  const mentors: Api.AdministratorPortfolioMentorRequest[][] = values.map((sem) =>
    sem.mentors.map((m) => ({ profileId: m.profileId, quota: m.quota }))
  )
  return { semesters, mentors }
}

export function sectionToSectionBody(
  section: Api.AdministratorPortfolioSectionDetails | null
): Api.AdministratorPortfolioSectionRequest {
  const init = getSectionInitialValues(section).section
  return sectionFormValuesToBody(init)
}

export function sectionToCompetenciesBody(
  section: Api.AdministratorPortfolioSectionDetails | null
): Api.AdministratorPortfolioCompetencyRequest {
  const { competencies } = getCompetenciesInitialValues(section)
  return competenciesFormValuesToBody(competencies)
}

export function sectionToSemestersAndMentors(section: Api.AdministratorPortfolioSectionDetails | null): {
  semesters: Api.AdministratorPortfolioSemesterRequest[]
  mentors: Api.AdministratorPortfolioMentorRequest[][]
} {
  const { semesters } = getSemestersInitialValues(section)
  return semestersFormValuesToBody(semesters)
}

export interface SectionPutBody {
  readonly section: Api.AdministratorPortfolioSectionRequest
  readonly competencies: Api.AdministratorPortfolioCompetencyRequest
  readonly semesters: readonly Api.AdministratorPortfolioSemesterRequest[]
  readonly mentors: readonly (readonly Api.AdministratorPortfolioMentorRequest[])[]
}

export function buildBodyWithSection(
  section: Api.AdministratorPortfolioSectionDetails | null,
  sectionValues: SectionFormValues
): SectionPutBody {
  const rest = sectionToSemestersAndMentors(section)
  return {
    section: sectionFormValuesToBody(sectionValues),
    competencies: sectionToCompetenciesBody(section),
    semesters: rest.semesters,
    mentors: rest.mentors,
  }
}

export function buildBodyWithCompetencies(
  section: Api.AdministratorPortfolioSectionDetails | null,
  competenciesValues: CompetencyFormValues[]
): SectionPutBody {
  const rest = sectionToSemestersAndMentors(section)
  return {
    section: sectionToSectionBody(section),
    competencies: competenciesFormValuesToBody(competenciesValues),
    semesters: rest.semesters,
    mentors: rest.mentors,
  }
}

export function buildBodyWithSemesters(
  section: Api.AdministratorPortfolioSectionDetails | null,
  semestersValues: SemesterFormValues[]
): SectionPutBody {
  const { semesters, mentors } = semestersFormValuesToBody(semestersValues)
  return {
    section: sectionToSectionBody(section),
    competencies: sectionToCompetenciesBody(section),
    semesters,
    mentors,
  }
}

export async function submitSection(params: {
  headers: Parameters<typeof Api.putAdministratorPortfolioSection>[0]['headers']
  id: string | undefined
  body: SectionPutBody
  navigate: (to: string) => void
  locale: string
  mutate: () => void
  t: Fn
}): Promise<void> {
  const { headers, id, body, navigate, locale, mutate, t } = params
  if (id != null) {
    await Api.putAdministratorPortfolioSection({
      headers,
      body: {
        section: body.section,
        competencies: body.competencies,
        mentors: body.mentors,
        semesters: body.semesters,
      },
      args: { id },
    })
  } else {
    await Api.postAdministratorPortfolioSection({
      headers,
      body: {
        section: body.section,
        competencies: body.competencies,
        mentors: body.mentors,
        semesters: body.semesters,
      },
    })
  }
  mutate()
  toast.success(t('common:updated_successfully'))
  navigate(`/${locale}/administrator/portfolio/sections`)
}
